import React, { useEffect, useState } from 'react';
import {
  Avatar, Button, Card, Col, Empty, Input, List, Row,
  Skeleton, Space, Statistic, Tag, Timeline, Typography, message,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowUpOutlined, ClockCircleOutlined,
  CommentOutlined, UserOutlined,
} from '@ant-design/icons';
import * as api from '../api/client';
import type { FeatureRequestItem, FeatureRequestComment, FeatureRequestStatusHistory } from '../api/client';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  PENDING_REVIEW: 'Pending Review',
  APPROVED_FOR_VOTING: 'Approved for Voting',
  UNDER_CONSIDERATION: 'Under Consideration',
  PLANNED: 'Planned',
  IN_DEVELOPMENT: 'In Development',
  RELEASED: 'Released',
  DECLINED: 'Declined',
  MERGED: 'Merged',
  ARCHIVED: 'Archived',
  DRAFT: 'Draft',
  NEEDS_CLARIFICATION: 'Needs Clarification',
};

const STATUS_TAG_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'orange',
  APPROVED_FOR_VOTING: 'blue',
  UNDER_CONSIDERATION: 'purple',
  PLANNED: 'cyan',
  IN_DEVELOPMENT: 'gold',
  RELEASED: 'green',
  DECLINED: 'red',
  MERGED: 'default',
  ARCHIVED: 'default',
  DRAFT: 'default',
  NEEDS_CLARIFICATION: 'warning',
};

const TIMELINE_COLORS: Record<string, string> = {
  APPROVED_FOR_VOTING: 'blue',
  RELEASED: 'green',
  DECLINED: 'red',
};

const IMPACT_TAG_COLORS: Record<string, string> = {
  LOW: 'default', MEDIUM: 'blue', HIGH: 'orange', CRITICAL: 'red',
};

const CATEGORY_LABELS: Record<string, string> = {
  AI_AGENTS: 'AI Agents', PLAYBOOKS: 'Playbooks', KNOWLEDGE_BASE: 'Knowledge Base',
  API: 'API', INTEGRATIONS: 'Integrations', DESKTOP_APP: 'Desktop App',
  UI_UX: 'UI / UX', ADMIN: 'Admin', SECURITY: 'Security', BILLING: 'Billing', OTHER: 'Other',
};

const VOTABLE_STATUSES = ['APPROVED_FOR_VOTING', 'UNDER_CONSIDERATION', 'PLANNED', 'IN_DEVELOPMENT'];

const formatDate = (d?: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return '—'; }
};

const formatDateTime = (d?: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return '—'; }
};

const initials = (name?: string) =>
  (name || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  featureRequestId: string;
  isAdmin?: boolean;
  adminEmail?: string;
  onBack: () => void;
}

const FeatureRequestDetails: React.FC<Props> = ({ featureRequestId, isAdmin = false, adminEmail = '', onBack }) => {
  const [item, setItem] = useState<FeatureRequestItem | null>(null);
  const [comments, setComments] = useState<FeatureRequestComment[]>([]);
  const [history, setHistory] = useState<FeatureRequestStatusHistory[]>([]);
  const [currentVote, setCurrentVote] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [voting, setVoting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getFeatureRequestById(featureRequestId, isAdmin ? adminEmail : undefined);
      setItem(res.data.item);
      setCurrentVote(res.data.currentUserVote || 0);
      setComments(res.data.comments || []);
      setHistory(res.data.statusHistory || []);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (featureRequestId) void load(); }, [featureRequestId]); // load when ID changes

  const toggleVote = async () => {
    if (!item) return;
    setVoting(true);
    try {
      if (currentVote > 0) await api.removeFeatureRequestVote(item.id);
      else await api.voteFeatureRequest(item.id);
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Vote action failed.');
    } finally {
      setVoting(false);
    }
  };

  const addComment = async () => {
    if (!item || !commentText.trim()) return;
    setCommenting(true);
    try {
      await api.addFeatureRequestComment(item.id, { comment: commentText.trim() }, isAdmin ? adminEmail : undefined);
      setCommentText('');
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Comment failed.');
    } finally {
      setCommenting(false);
    }
  };

  if (!featureRequestId) return null;

  if (loading || !item) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16 }}>
          Back
        </Button>
        <Skeleton active />
      </div>
    );
  }

  const canVote = VOTABLE_STATUSES.includes(item.status);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Back</Button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Title level={4} style={{ margin: '0 0 6px' }}>{item.title}</Title>
          <Space wrap size={6}>
            <Tag color={STATUS_TAG_COLORS[item.status] || 'default'}>
              {STATUS_LABELS[item.status] || item.status}
            </Tag>
            <Tag>{CATEGORY_LABELS[item.category] || item.category}</Tag>
            <Tag color={IMPACT_TAG_COLORS[item.impactLevel] || 'default'}>{item.impactLevel}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <UserOutlined /> {item.createdByName || item.createdByUserId}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined /> Updated {formatDate(item.updatedOn || item.createdOn)}
            </Text>
          </Space>
        </div>
      </div>

      {/* Body */}
      <Row gutter={[16, 16]}>
        {/* Left column */}
        <Col xs={24} lg={16}>
          <Card title="Overview" style={{ marginBottom: 16 }}>
            {item.description ? (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Description</Text>
                <Paragraph style={{ marginTop: 4 }}>{item.description}</Paragraph>
              </div>
            ) : null}
            {item.problemStatement ? (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Problem Statement</Text>
                <Paragraph style={{ marginTop: 4 }}>{item.problemStatement}</Paragraph>
              </div>
            ) : null}
            {item.proposedSolution ? (
              <div>
                <Text strong>Proposed Solution</Text>
                <Paragraph style={{ marginTop: 4 }}>{item.proposedSolution}</Paragraph>
              </div>
            ) : null}
            {!item.description && !item.problemStatement && !item.proposedSolution && (
              <Empty description="No details provided." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          <Card title="Status Timeline">
            {history.length === 0 ? (
              <Empty description="No timeline events yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Timeline>
                {history.map(h => (
                  <Timeline.Item
                    key={h.id}
                    color={TIMELINE_COLORS[h.newStatus] || 'blue'}
                  >
                    <Text strong>{STATUS_LABELS[h.newStatus] || h.newStatus}</Text>
                    {h.note && (
                      <div><Text type="secondary">{h.note}</Text></div>
                    )}
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatDateTime(h.createdOn)}
                        {h.changedByName ? ` · ${h.changedByName}` : ''}
                      </Text>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Card>
        </Col>

        {/* Right column */}
        <Col xs={24} lg={8}>
          {/* Voting */}
          <Card style={{ marginBottom: 16, textAlign: 'center' }}>
            <Statistic
              title="Community Votes"
              value={item.voteCount}
              prefix={<ArrowUpOutlined />}
              style={{ marginBottom: 12 }}
            />
            <Button
              type={currentVote > 0 ? 'default' : 'primary'}
              icon={<ArrowUpOutlined />}
              disabled={!canVote}
              loading={voting}
              onClick={toggleVote}
              block
            >
              {currentVote > 0 ? 'Remove Vote' : 'Vote for This'}
            </Button>
            {!canVote && (
              <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                Voting is available after admin approval.
              </Text>
            )}
          </Card>

          {/* Metadata */}
          <Card title="Details" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Requested by</Text>
                <div><Text strong>{item.createdByName || item.createdByUserId}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Submitted</Text>
                <div><Text>{formatDate(item.createdOn)}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Last updated</Text>
                <div><Text>{formatDate(item.updatedOn)}</Text></div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Comments</Text>
                <div>
                  <Text><CommentOutlined /> {item.commentCount}</Text>
                </div>
              </div>
            </Space>
          </Card>

          {/* Comments */}
          <Card title={`Comments (${comments.length})`}>
            {comments.length > 0 ? (
              <List
                dataSource={comments}
                renderItem={c => (
                  <List.Item style={{ alignItems: 'flex-start' }}>
                    <List.Item.Meta
                      avatar={
                        <Avatar size="small" style={{ backgroundColor: '#1677ff' }}>
                          {initials(c.userDisplayName)}
                        </Avatar>
                      }
                      title={
                        <Space size={6}>
                          <Text strong style={{ fontSize: 13 }}>{c.userDisplayName}</Text>
                          {c.isAdminComment && <Tag color="blue" style={{ margin: 0 }}>Admin</Tag>}
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {formatDateTime(c.createdOn)}
                          </Text>
                        </Space>
                      }
                      description={c.comment}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No comments yet." image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginBottom: 12 }} />
            )}
            <div style={{ marginTop: 12 }}>
              <TextArea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                rows={3}
                placeholder="Add a comment…"
                style={{ marginBottom: 8 }}
              />
              <Button
                type="primary"
                onClick={addComment}
                disabled={!commentText.trim()}
                loading={commenting}
                block
              >
                Comment
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FeatureRequestDetails;
